import { ComponentType } from 'react';

import { APPLICATION_TITLE, USER_CATEGORY } from '../constants';
// PrimaryStep and SubStep are re-exported for backward compatibility.
// New imports should come from './steps' to avoid the React dependency chain.
export { PrimaryStep, SubStep } from './steps';
import { PrimaryStep, SubStep } from './steps';
import {
  type AddressInformationFormData,
  AddressInformationPage,
  type ContactInformationFormData,
  ContactInformationPage,
  MyPersonalDetailsPage,
  type PersonalDetailsFormData,
} from '../steps/personal-details';
import {
  type BirthInformationFormData,
  BirthInformationPage,
  type CitizenshipStatusFormData,
  CitizenshipStatusPage,
  type ImmigrationDocumentFormData,
  ImmigrationDocumentPage,
  type PassportInformationFormData,
  PassportInformationPage,
  type ResidencyInformationFormData,
  ResidencyInformationPage,
  type USEntryInformationFormData,
  USEntryInformationPage,
} from '../steps/citizenship-details';
import {
  type ContractInformationFormData,
  ContractInformationPage,
  type WorkContactInformationFormData,
  WorkContactInformationPage,
} from '../steps/contract-details';
import {
  type CertificationFormData,
  CertificationPage,
  type CourtMartialFormData,
  CourtMartialPage,
  type CriminalCorrectionFormData,
  CriminalCorrectionPage,
  type CurrentViolationsFormData,
  CurrentViolationsPage,
  type FederalDebtFormData,
  FederalDebtPage,
  type JobDismissalFormData,
  JobDismissalPage,
  type MilitaryExperienceFormData,
  MilitaryExperiencePage,
  type RelativesFormData,
  RelativesPage,
  type RetirementPayFormData,
  RetirementPayPage,
  type SelectiveServiceFormData,
  SelectiveServicePage,
} from '../steps/of-306-questions';
import { type DocumentUploadFormData, DocumentUploadPage } from '../steps/document-upload';
import FinalReviewPage from '../steps/final-review/FinalReviewPage';
import ConfirmationPage from '../steps/confirmation/ConfirmationPage';
import {
  custom,
  email,
  FieldValidationConfig,
  isBooleanSet,
  isFieldSet,
  isNonEmptyString,
  isValidEmail,
  isValidPassportNumber,
  isValidPhoneNumber,
  isValidSSN,
  isValidVisaNumber,
  isValidZipCode,
  passportNumber,
  phoneNumber,
  required,
  requiredAs,
  requiredChoice,
  ssn,
  ssnConfirmMatch,
  validateYesNoWithList,
  visaNumber,
  zipCode,
} from '../utils/fieldValidation';
import { FieldErrors } from '../types';

/**
 * Generic type for substep form data.
 *
 * NOTE: This uses 'any' to allow proper generic type inference in WorkflowSubStep<T>.
 * Each substep component specifies its own concrete FormData type (e.g., PersonalDetailsFormData).
 *
 * IMPORTANT FOR DEVELOPERS:
 * When adding a new substep:
 * 1. Define a specific interface for your form data (e.g., MyStepFormData)
 * 2. Export the interface from your step component
 * 3. Import and use it in the workflow config with proper typing:
 *    component: MyStepPage as ComponentType<WorkflowSubStepProps<MyStepFormData>>
 *
 * The 'any' here is intentional to enable TypeScript's generic type system.
 * Without it, we would lose type safety in the individual step components.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = any; // Empty object for steps with no form data (e.g., FinalReviewPage, ConfirmationPage)

export const WORKFLOW_TITLE = APPLICATION_TITLE;

export interface WorkflowSubStep<T = FormData> {
  id: string;
  title: string;
  component: ComponentType<WorkflowSubStepProps<T>>;
  validate?: (substepData: T, allFormData: WorkflowFormData) => boolean;
  fieldValidation?: FieldValidationConfig;
  defaultData?: T;
  optional?: boolean;
  shouldSkip?: (allFormData: WorkflowFormData) => boolean;
  /**
   * Why the sub-step refuses to advance when no field is at fault (a document still
   * being scanned). Shown instead of the generic "Some required information is
   * missing" alert, which would send the user looking for a field that is fine.
   */
  blockedReason?: (substepData: T, allFormData: WorkflowFormData) => { heading: string; text: string } | undefined;
  /**
   * Keep the step-level alert visible even when fields carry their own errors. For a
   * sub-step whose field errors are screen-reader only (missing uploads), the alert is
   * the only error a sighted user would otherwise see.
   */
  keepIncompleteAlert?: boolean;
  /**
   * Trailing noun for RequiredFieldsLegend (for example "fields" or "uploads").
   * Defaults to "fields" when omitted.
   */
  requiredFieldsText?: string;
  /**
   * Called before navigating to the next substep.
   * Can be used to auto-save draft data or perform cleanup.
   * Should return the updated substep data.
   */
  onBeforeNext?: (substepData: T, allFormData: WorkflowFormData) => T;
}

export interface WorkflowStep {
  id: string;
  title: string;
  subSteps: WorkflowSubStep[];
  optional?: boolean;
}

export interface WorkflowSubStepProps<T = FormData> {
  data: T;
  updateData: (data: T) => void;
  allFormData: WorkflowFormData;
  fieldErrors?: FieldErrors;
}

export interface WorkflowFormData {
  [stepId: string]: {
    [substepId: string]: FormData;
  };
}

export interface WorkflowConfig {
  steps: WorkflowStep[];
}

const MILITARY_DATE_RANGE_ERROR = 'The "To" date must be after the "From" date.';

function hasValidMilitaryDateRange(draft: MilitaryExperienceFormData['draftMilitaryExperience']): boolean {
  if (!draft.dateServedFrom || !draft.dateServedTo) return true;

  return new Date(draft.dateServedFrom) <= new Date(draft.dateServedTo);
}

function isCompleteMilitaryExperienceDraft(draft: MilitaryExperienceFormData['draftMilitaryExperience']): boolean {
  return (
    isNonEmptyString(draft.branch) &&
    isNonEmptyString(draft.dateServedFrom) &&
    isNonEmptyString(draft.dateServedTo) &&
    isNonEmptyString(draft.dischargeType) &&
    hasValidMilitaryDateRange(draft)
  );
}

function isEmptyMilitaryExperienceDraft(draft: MilitaryExperienceFormData['draftMilitaryExperience']): boolean {
  return !draft.branch && !draft.dateServedFrom && !draft.dateServedTo && !draft.dischargeType;
}

function isCompleteRetiredPayDraft(draft: RetirementPayFormData['draftRetiredPay']): boolean {
  if (!isNonEmptyString(draft.payType)) return false;
  if (draft.payType === 'other' && !isNonEmptyString(draft.otherPayType)) return false;
  if (!isBooleanSet(draft.stillReceivingPay)) return false;

  return draft.stillReceivingPay === true
    ? isNonEmptyString(draft.receiptStartDate)
    : isNonEmptyString(draft.receiptEndDate);
}

function isEmptyRetiredPayDraft(draft: RetirementPayFormData['draftRetiredPay']): boolean {
  return (
    !draft.payType &&
    !draft.otherPayType &&
    draft.stillReceivingPay === null &&
    !draft.receiptStartDate &&
    !draft.receiptEndDate
  );
}

interface UploadedFileState {
  documentId?: string;
  error?: string;
  id?: string;
  isUploading?: boolean;
  uploadStatus?: string;
}

/** At least one file finished uploading and is associated with the application. */
function hasAssociatedFile(files?: UploadedFileState[]): boolean {
  return Boolean(
    files &&
      files.some(
        (file) => !file.error && !file.isUploading && (file.uploadStatus === 'associated' || Boolean(file.documentId)),
      ),
  );
}

/** Which identity documents the applicant owes, from their citizenship answers. */
function uploadBranch(allFormData?: Record<string, unknown>): {
  isNonUSCitizen: boolean;
  isPermanentResident: boolean;
} {
  const citizenship = (allFormData as WorkflowFormData | undefined)?.[PrimaryStep.CITIZENSHIP_DETAILS];
  const isNonUSCitizen =
    (citizenship?.[SubStep.CITIZENSHIP_STATUS] as { isUSCitizen?: boolean | null } | undefined)?.isUSCitizen === false;
  const isPermanentResident =
    isNonUSCitizen &&
    (citizenship?.[SubStep.RESIDENCY_INFORMATION] as { isPermanentResident?: boolean | null } | undefined)
      ?.isPermanentResident === true;

  return { isNonUSCitizen, isPermanentResident };
}

/** An uploaded document the applicant owes is still being scanned for malware. */
function isUploadScanInProgress(data: DocumentUploadFormData, allFormData?: Record<string, unknown>): boolean {
  const { isNonUSCitizen, isPermanentResident } = uploadBranch(allFormData);
  const applicableFiles = [
    ...(data.pivTrainingCertificate ?? []),
    ...(data.resume ?? []),
    ...(data.passport ?? []),
    ...(data.legalDocuments ?? []),
    ...(data.otherDocuments ?? []),
    ...(isPermanentResident ? data.residencyDocument ?? [] : []),
    ...(isNonUSCitizen && !isPermanentResident ? data.usVisaDocument ?? [] : []),
    ...(isNonUSCitizen ? data.residencySupportingDocuments ?? [] : []),
  ];

  return applicableFiles.some(
    (file) => file.uploadStatus === 'associated' && (file.scanStatus === 'PENDING' || file.scanStatus === 'SCANNING'),
  );
}

/**
 * ACP-2092 / ACP-2077 (Section 508): a missing required document used to block with only
 * the step-level "Some required information is missing" alert, naming no upload. Each
 * rule mirrors one condition of the sub-step's `validate`, and applies only to the
 * documents this applicant's citizenship answers call for.
 */
function requiredUpload(applies: (branch: ReturnType<typeof uploadBranch>) => boolean, message: string) {
  return custom(
    (value: unknown, _data?: Record<string, unknown>, allFormData?: Record<string, unknown>) =>
      !applies(uploadBranch(allFormData)) || hasAssociatedFile(value as UploadedFileState[] | undefined),
    message,
  );
}

/**
 * ACP-2092 / ACP-2077 (Section 508): whether a list sub-step's draft entry may be left
 * blank — only once an entry has been saved, and only while the draft is untouched.
 * `validateYesNoWithList` rejects a started-but-incomplete draft even with saved entries,
 * so the per-field rules must still flag it: before this, a second entry left half-done
 * blocked Next with no field marked, only the step-level fallback alert.
 */
function isDraftOptional(allData: Record<string, unknown> | undefined, listKey: string, draftKey: string): boolean {
  const saved = allData?.[listKey] as Array<unknown> | undefined;
  if (!saved || saved.length === 0) return false;
  if (allData?.isAddingEntry === true) return false;

  const draft = allData?.[draftKey] as Record<string, unknown> | undefined;

  return !draft || Object.values(draft).every((value) => value === '' || value === null || value === undefined);
}

/**
 * ACP-2092 / ACP-2077 (Section 508): `validate` rejects an incomplete retired pay entry,
 * but without a matching `fieldValidation` rule nothing marked which field was missing —
 * Next fell back to the step-level "Some required information is missing" alert, so a
 * screen reader user had to tab through the form to find the field. Each rule mirrors
 * `isCompleteRetiredPayDraft` for one field, and only applies while that field is on
 * screen: the entry is required when no pay has been saved yet, or when one has been
 * started.
 */
function requiredInRetiredPayDraft(
  isMissing: (draft: RetirementPayFormData['draftRetiredPay']) => boolean,
  message: string,
) {
  return custom((_value: unknown, allData?: Record<string, unknown>) => {
    if (allData?.hasRetirementPay !== true) return true;

    const draft = allData?.draftRetiredPay as RetirementPayFormData['draftRetiredPay'] | undefined;
    if (!draft || isDraftOptional(allData, 'retiredPays', 'draftRetiredPay')) return true;

    return !isMissing(draft);
  }, message);
}

export const workflowConfig: WorkflowConfig = {
  steps: [
    {
      id: PrimaryStep.PERSONAL_DETAILS,
      subSteps: [
        {
          component: MyPersonalDetailsPage,
          defaultData: {
            dateOfBirth: '',
            firstName: '',
            hasMiddleName: null,
            lastName: '',
            middleName: '',
            otherNames: '',
            ssn: '',
            ssnConfirm: '',
            suffix: '',
          },
          fieldValidation: {
            dateOfBirth: [required],
            firstName: [required],
            hasMiddleName: [requiredChoice],
            lastName: [required],
            middleName: [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasMiddleName) {
                  if (typeof value === 'string') {
                    return value.trim().length > 0;
                  }

                  return false;
                }

                return true; // Not required if hasMiddleName is false
              }, 'Legal Middle Name is required'),
            ],
            ssn: [required, ssn],
            ssnConfirm: [required, ssn, ssnConfirmMatch],
          },
          id: SubStep.BASIC_INFO,
          title: 'Basic Information',
          validate: (data: PersonalDetailsFormData): boolean => {
            if (!data) return false;

            // Check if hasMiddleName is explicitly set (not undefined)
            const hasMiddleNameSet = isBooleanSet(data.hasMiddleName);

            // If hasMiddleName is true, middleName must be provided
            const middleNameValid = data.hasMiddleName === true ? isNonEmptyString(data.middleName) : true;

            // Check that SSN and SSN confirmation match
            const ssnMatch = data.ssn === data.ssnConfirm;

            return (
              isNonEmptyString(data.firstName) &&
              isNonEmptyString(data.lastName) &&
              isFieldSet(data.dateOfBirth) &&
              isValidSSN(data.ssn) &&
              isValidSSN(data.ssnConfirm) &&
              ssnMatch &&
              hasMiddleNameSet &&
              middleNameValid
            );
          },
        },
        {
          component: AddressInformationPage,
          defaultData: {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            workFromHome: null,
            zipCode: '',
          },
          fieldValidation: {
            addressLine1: [required],
            city: [required],
            state: [required],
            workFromHome: [requiredChoice],
            zipCode: [required, zipCode],
          },
          id: SubStep.ADDRESS_INFO,
          title: 'Address Information',
          validate: (data: AddressInformationFormData): boolean => {
            if (!data) return false;

            return (
              isNonEmptyString(data.addressLine1) &&
              isNonEmptyString(data.city) &&
              isFieldSet(data.state) &&
              isValidZipCode(data.zipCode) &&
              isBooleanSet(data.workFromHome)
            );
          },
        },
        {
          component: ContactInformationPage,
          defaultData: {
            phoneNumber: '',
            registrationEmail: '',
            userCategory: USER_CATEGORY.VALUE,
          },
          fieldValidation: {
            phoneNumber: [required, phoneNumber],
            registrationEmail: [required, email],
          },
          id: SubStep.CONTACT_INFO,
          title: 'Contact Information',
          validate: (data: ContactInformationFormData): boolean => {
            if (!data) return false;

            return (
              isValidEmail(data.registrationEmail) &&
              isValidPhoneNumber(data.phoneNumber) &&
              isFieldSet(data.userCategory)
            );
          },
        },
      ],
      title: 'My Personal Details',
    },
    {
      id: PrimaryStep.CITIZENSHIP_DETAILS,
      subSteps: [
        {
          component: CitizenshipStatusPage,
          defaultData: {
            isUSCitizen: null,
          },
          fieldValidation: {
            isUSCitizen: [requiredChoice],
          },
          id: SubStep.CITIZENSHIP_STATUS,
          title: 'Citizenship Status',
          validate: (data: CitizenshipStatusFormData): boolean => {
            if (!data) return false;

            return data.isUSCitizen !== null && data.isUSCitizen !== undefined;
          },
        },
        {
          component: BirthInformationPage,
          defaultData: {
            birthCity: '',
            birthCountry: '',
            birthCountryCode: '',
            birthState: '',
            sex: '',
          },
          fieldValidation: {
            birthCity: [required],
            birthCountry: [required],
            birthState: [required],
            sex: [required],
          },
          id: SubStep.BIRTH_INFORMATION,
          shouldSkip: (allFormData: WorkflowFormData): boolean => {
            // Skip this substep if user is a US citizen or if not answered yet
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;

            return citizenshipData?.isUSCitizen === null;
          },
          title: 'Birth Information',
          validate: (data: BirthInformationFormData): boolean => {
            if (!data) return false;

            return (
              isNonEmptyString(data.sex) &&
              isNonEmptyString(data.birthCountry) &&
              isNonEmptyString(data.birthCity) &&
              isNonEmptyString(data.birthState)
            );
          },
        },
        {
          component: ResidencyInformationPage,
          defaultData: {
            citizenshipCountry: '',
            citizenshipCountryCode: '',
            isPermanentResident: null,
          },
          fieldValidation: {
            citizenshipCountry: [required],
            isPermanentResident: [requiredChoice],
          },
          id: SubStep.RESIDENCY_INFORMATION,
          shouldSkip: (allFormData: WorkflowFormData): boolean => {
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;

            return citizenshipData?.isUSCitizen !== false;
          },
          title: 'Residency Information',
          validate: (data: ResidencyInformationFormData, allFormData: WorkflowFormData): boolean => {
            // Only validate if user is not a US citizen
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;
            if (citizenshipData?.isUSCitizen === true) {
              return true; // Skip validation if US citizen
            }

            if (!data) return false;

            return isBooleanSet(data.isPermanentResident) && isNonEmptyString(data.citizenshipCountry);
          },
        },
        {
          component: USEntryInformationPage,
          defaultData: {
            entryVisaType: '',
            firstEntryDate: '',
            yearsInUS: '',
          },
          fieldValidation: {
            entryVisaType: [requiredAs('Entry visa type is required')],
            firstEntryDate: [requiredAs('First entry date is required')],
            yearsInUS: [required],
          },
          id: SubStep.US_ENTRY_INFORMATION,
          shouldSkip: (allFormData: WorkflowFormData): boolean => {
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;
            const residencyData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] as
              | ResidencyInformationFormData
              | undefined;

            return citizenshipData?.isUSCitizen !== false || residencyData?.isPermanentResident !== false;
          },
          title: 'U.S. Entry Information',
          validate: (data: USEntryInformationFormData, allFormData: WorkflowFormData): boolean => {
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;
            const residencyData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] as
              | ResidencyInformationFormData
              | undefined;
            if (citizenshipData?.isUSCitizen !== false || residencyData?.isPermanentResident !== false) {
              return true;
            }

            if (!data) return false;

            return (
              isNonEmptyString(data.firstEntryDate) &&
              isNonEmptyString(data.entryVisaType) &&
              isNonEmptyString(data.yearsInUS)
            );
          },
        },
        {
          component: ImmigrationDocumentPage,
          defaultData: {
            documentType: 'Visa Number',
            immigrationDate: '',
            visaNumber: '',
          },
          fieldValidation: {
            immigrationDate: [required],
            visaNumber: [required, visaNumber],
          },
          id: SubStep.IMMIGRATION_DOCUMENT,
          shouldSkip: (allFormData: WorkflowFormData): boolean => {
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;
            const residencyData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] as
              | ResidencyInformationFormData
              | undefined;

            return citizenshipData?.isUSCitizen !== false || residencyData?.isPermanentResident !== false;
          },
          title: 'Immigration Information',
          validate: (data: ImmigrationDocumentFormData, allFormData: WorkflowFormData): boolean => {
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;
            const residencyData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] as
              | ResidencyInformationFormData
              | undefined;
            if (citizenshipData?.isUSCitizen !== false || residencyData?.isPermanentResident !== false) {
              return true;
            }

            if (!data) return false;

            return isNonEmptyString(data.immigrationDate) && isValidVisaNumber(data.visaNumber);
          },
        },
        {
          component: PassportInformationPage,
          defaultData: {
            passportExpirationDate: '',
            passportIssuanceCountry: '',
            passportIssuanceCountryCode: '',
            passportIssueDate: '',
            passportNumber: '',
            passportType: '',
          },
          fieldValidation: {
            passportExpirationDate: [required],
            passportIssuanceCountry: [required],
            passportIssueDate: [required],
            passportNumber: [required, passportNumber],
            passportType: [required],
          },
          id: SubStep.PASSPORT_INFORMATION,
          shouldSkip: (allFormData: WorkflowFormData): boolean => {
            // Skip this substep if user is a US citizen or if not answered yet
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;

            return citizenshipData?.isUSCitizen !== false;
          },
          title: 'Passport Information',
          validate: (data: PassportInformationFormData, allFormData: WorkflowFormData): boolean => {
            // Only validate if user is not a US citizen
            const citizenshipData = allFormData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] as
              | CitizenshipStatusFormData
              | undefined;
            if (citizenshipData?.isUSCitizen === true) {
              return true; // Skip validation if US citizen
            }

            if (!data) return false;

            return (
              isNonEmptyString(data.passportIssuanceCountry) &&
              isNonEmptyString(data.passportType) &&
              isValidPassportNumber(data.passportNumber) &&
              isNonEmptyString(data.passportIssueDate) &&
              isNonEmptyString(data.passportExpirationDate)
            );
          },
        },
      ],
      title: 'My Citizenship Details',
    },
    {
      id: PrimaryStep.CONTRACT_DETAILS,
      subSteps: [
        {
          component: ContractInformationPage,
          defaultData: {
            cmsRegion: '',
            company: '',
            contractNumber: '',
            laborCategory: '',
            requestJustification: '',
          },
          fieldValidation: {
            cmsRegion: [required],
            requestJustification: [required],
          },
          id: SubStep.CONTRACT_INFO,
          title: 'Contract Information',
          validate: (data: ContractInformationFormData): boolean => {
            if (!data) return false;

            return (
              !!data.cmsRegion &&
              !!data.requestJustification &&
              data.cmsRegion.trim().length > 0 &&
              data.requestJustification.trim().length > 0
            );
          },
        },
        {
          component: WorkContactInformationPage,
          defaultData: {
            workAddressCity: '',
            workAddressLine1: '',
            workAddressLine2: '',
            workAddressState: '',
            workAddressZipCode: '',
            workEmail: '',
            workExtension: '',
            workPhoneNumber: '',
          },
          fieldValidation: {
            workAddressCity: [required],
            workAddressLine1: [required],
            workAddressState: [required],
            workAddressZipCode: [required, zipCode],
            workEmail: [required, email],
            workPhoneNumber: [required, phoneNumber],
          },
          id: SubStep.WORK_CONTACT_INFO,
          title: 'Work Contact Information',
          validate: (data: WorkContactInformationFormData): boolean => {
            if (!data) return false;

            return (
              isValidEmail(data.workEmail) &&
              isValidPhoneNumber(data.workPhoneNumber) &&
              isNonEmptyString(data.workAddressLine1) &&
              isNonEmptyString(data.workAddressCity) &&
              isFieldSet(data.workAddressState) &&
              isValidZipCode(data.workAddressZipCode)
            );
          },
        },
      ],
      title: 'Contract Details',
    },
    {
      id: PrimaryStep.OF_306_QUESTIONS,
      subSteps: [
        {
          component: SelectiveServicePage,
          defaultData: {
            bornMaleAfter1959: null,
            registeredWithSelectiveService: null,
            selectiveServiceExplanation: '',
          },
          fieldValidation: {
            bornMaleAfter1959: [requiredChoice],
            registeredWithSelectiveService: [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                // Only required if bornMaleAfter1959 is true
                if (allData?.bornMaleAfter1959 !== true) return true;

                return value === true || value === false;
              }, 'Select Yes or No'),
            ],
            selectiveServiceExplanation: [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                // Only required if bornMaleAfter1959 is true AND registeredWithSelectiveService is false
                if (allData?.bornMaleAfter1959 !== true) return true;
                if (allData?.registeredWithSelectiveService !== false) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Please explain why you have not registered with the Selective Service System'),
            ],
          },
          id: SubStep.SELECTIVE_SERVICE,
          title: 'Selective Service Registration',
          validate: (data: SelectiveServiceFormData): boolean => {
            if (!data) return false;

            // bornMaleAfter1959 is always required
            // If answered false (not born male after 1959), we're done
            // If answered true, registeredWithSelectiveService is required
            // If registered (true), we're done
            // If not registered (false), explanation is required
            if (!isBooleanSet(data.bornMaleAfter1959)) return false;
            if (data.bornMaleAfter1959 === false) return true;
            if (!isBooleanSet(data.registeredWithSelectiveService)) return false;
            if (data.registeredWithSelectiveService === true) return true;

            return isNonEmptyString(data.selectiveServiceExplanation);
          },
        },
        {
          component: MilitaryExperiencePage,
          defaultData: {
            draftMilitaryExperience: {
              branch: '',
              dateServedFrom: '',
              dateServedTo: '',
              dischargeType: '',
            },
            hasMilitaryExperience: null,
            isAddingEntry: false,
            militaryExperiences: [],
          },
          fieldValidation: {
            'draftMilitaryExperience.branch': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                // Only required if hasMilitaryExperience is true, unless an experience is saved and the draft is untouched
                if (allData?.hasMilitaryExperience !== true) return true;

                if (isDraftOptional(allData, 'militaryExperiences', 'draftMilitaryExperience')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Branch of the Military is required'),
            ],
            'draftMilitaryExperience.dateServedFrom': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasMilitaryExperience !== true) return true;

                if (isDraftOptional(allData, 'militaryExperiences', 'draftMilitaryExperience')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Date Served From is required'),
            ],
            'draftMilitaryExperience.dateServedTo': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasMilitaryExperience !== true) return true;

                if (isDraftOptional(allData, 'militaryExperiences', 'draftMilitaryExperience')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Date Served To is required'),
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasMilitaryExperience !== true) return true;
                if (typeof value !== 'string' || value.trim().length === 0) return true;

                const draft = allData?.draftMilitaryExperience as
                  | MilitaryExperienceFormData['draftMilitaryExperience']
                  | undefined;
                if (!draft) return true;

                return hasValidMilitaryDateRange(draft);
              }, MILITARY_DATE_RANGE_ERROR),
            ],
            'draftMilitaryExperience.dischargeType': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasMilitaryExperience !== true) return true;

                if (isDraftOptional(allData, 'militaryExperiences', 'draftMilitaryExperience')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Type of Discharge is required'),
            ],
            hasMilitaryExperience: [requiredChoice],
          },
          id: SubStep.MILITARY_EXPERIENCE,
          onBeforeNext: (data: MilitaryExperienceFormData): MilitaryExperienceFormData => {
            if (data.hasMilitaryExperience !== true) return data;

            const draft = data.draftMilitaryExperience;
            const isDraftEmpty = isEmptyMilitaryExperienceDraft(draft);
            const isDraftComplete = isCompleteMilitaryExperienceDraft(draft);

            if (!isDraftEmpty && isDraftComplete) {
              const newItem = {
                data: { ...draft },
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };

              return {
                ...data,
                draftMilitaryExperience: {
                  branch: '',
                  dateServedFrom: '',
                  dateServedTo: '',
                  dischargeType: '',
                },
                isAddingEntry: false,
                militaryExperiences: [...data.militaryExperiences, newItem],
              };
            }

            return data;
          },
          title: 'Military Experience',
          validate: (data: MilitaryExperienceFormData): boolean => {
            if (!data) return false;

            // hasMilitaryExperience is always required
            // If answered false (no military experience), we're done
            // If answered true, must have at least one military experience entry
            // (either saved experiences OR a complete draft with all required fields)
            return validateYesNoWithList(
              data.hasMilitaryExperience,
              data.militaryExperiences,
              data.draftMilitaryExperience,
              isCompleteMilitaryExperienceDraft,
              isEmptyMilitaryExperienceDraft,
            );
          },
        },
        {
          component: CriminalCorrectionPage,
          defaultData: {
            criminalCorrections: [],
            draftCriminalCorrection: {
              correctionDate: '',
              correctionDetails: '',
            },
            hasCriminalCorrection: null,
            isAddingEntry: false,
          },
          fieldValidation: {
            'draftCriminalCorrection.correctionDate': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasCriminalCorrection !== true) return true;

                if (isDraftOptional(allData, 'criminalCorrections', 'draftCriminalCorrection')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Date of the conviction, imprisonment, probation, or parole is required'),
            ],
            'draftCriminalCorrection.correctionDetails': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasCriminalCorrection !== true) return true;

                if (isDraftOptional(allData, 'criminalCorrections', 'draftCriminalCorrection')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Explanation of the conviction, imprisonment, probation, or parole is required'),
            ],
            hasCriminalCorrection: [requiredChoice],
          },
          id: SubStep.CRIMINAL_CORRECTION,
          onBeforeNext: (data: CriminalCorrectionFormData): CriminalCorrectionFormData => {
            if (data.hasCriminalCorrection !== true) return data;

            const draft = data.draftCriminalCorrection;
            const isDraftEmpty = !draft.correctionDate && !draft.correctionDetails;
            const isDraftComplete = isNonEmptyString(draft.correctionDate) && isNonEmptyString(draft.correctionDetails);

            if (!isDraftEmpty && isDraftComplete) {
              const newItem = {
                data: { ...draft },
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };

              return {
                ...data,
                criminalCorrections: [...data.criminalCorrections, newItem],
                draftCriminalCorrection: {
                  correctionDate: '',
                  correctionDetails: '',
                },
                isAddingEntry: false,
              };
            }

            return data;
          },
          title: 'Criminal Corrections',
          validate: (data: CriminalCorrectionFormData): boolean => {
            if (!data) return false;

            // hasCriminalCorrection is always required
            // If answered false (no criminal/correctional history), we're done
            // If answered true, must have at least one correction entry
            // (either saved corrections OR a complete draft with date and details)
            return validateYesNoWithList(
              data.hasCriminalCorrection,
              data.criminalCorrections,
              data.draftCriminalCorrection,
              (draft) => isNonEmptyString(draft.correctionDate) && isNonEmptyString(draft.correctionDetails),
              (draft) => !draft.correctionDate && !draft.correctionDetails,
            );
          },
        },
        {
          component: CourtMartialPage,
          defaultData: {
            courtMartials: [],
            draftCourtMartial: {
              courtMartialDate: '',
              courtMartialDetails: '',
            },
            hasCourtMartial: null,
            isAddingEntry: false,
          },
          fieldValidation: {
            'draftCourtMartial.courtMartialDate': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasCourtMartial !== true) return true;

                if (isDraftOptional(allData, 'courtMartials', 'draftCourtMartial')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Date of the court-martial is required'),
            ],
            'draftCourtMartial.courtMartialDetails': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasCourtMartial !== true) return true;

                if (isDraftOptional(allData, 'courtMartials', 'draftCourtMartial')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Explanation of the court-martial is required'),
            ],
            hasCourtMartial: [requiredChoice],
          },
          id: SubStep.COURT_MARTIAL,
          onBeforeNext: (data: CourtMartialFormData): CourtMartialFormData => {
            if (data.hasCourtMartial !== true) return data;

            const draft = data.draftCourtMartial;
            const isDraftEmpty = !draft.courtMartialDate && !draft.courtMartialDetails;
            const isDraftComplete =
              isNonEmptyString(draft.courtMartialDate) && isNonEmptyString(draft.courtMartialDetails);

            if (!isDraftEmpty && isDraftComplete) {
              const newItem = {
                data: { ...draft },
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };

              return {
                ...data,
                courtMartials: [...data.courtMartials, newItem],
                draftCourtMartial: {
                  courtMartialDate: '',
                  courtMartialDetails: '',
                },
                isAddingEntry: false,
              };
            }

            return data;
          },
          title: 'Court-Martial',
          validate: (data: CourtMartialFormData): boolean => {
            if (!data) return false;

            // hasCourtMartial is always required
            // If answered false (no court-martial history), we're done
            // If answered true, must have at least one court-martial entry
            // (either saved court-martials OR a complete draft with date and details)
            return validateYesNoWithList(
              data.hasCourtMartial,
              data.courtMartials,
              data.draftCourtMartial,
              (draft) => isNonEmptyString(draft.courtMartialDate) && isNonEmptyString(draft.courtMartialDetails),
              (draft) => !draft.courtMartialDate && !draft.courtMartialDetails,
            );
          },
        },
        {
          component: CurrentViolationsPage,
          defaultData: {
            draftViolation: {
              violationDate: '',
              violationDetails: '',
            },
            hasCurrentViolations: null,
            isAddingEntry: false,
            violations: [],
          },
          fieldValidation: {
            'draftViolation.violationDate': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasCurrentViolations !== true) return true;

                if (isDraftOptional(allData, 'violations', 'draftViolation')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Date of the violation is required'),
            ],
            'draftViolation.violationDetails': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasCurrentViolations !== true) return true;

                if (isDraftOptional(allData, 'violations', 'draftViolation')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Explanation of the violation is required'),
            ],
            hasCurrentViolations: [requiredChoice],
          },
          id: SubStep.CURRENT_VIOLATIONS,
          onBeforeNext: (data: CurrentViolationsFormData): CurrentViolationsFormData => {
            if (data.hasCurrentViolations !== true) return data;

            const draft = data.draftViolation;
            const isDraftEmpty = !draft.violationDate && !draft.violationDetails;
            const isDraftComplete = isNonEmptyString(draft.violationDate) && isNonEmptyString(draft.violationDetails);

            if (!isDraftEmpty && isDraftComplete) {
              const newItem = {
                data: { ...draft },
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };

              return {
                ...data,
                draftViolation: {
                  violationDate: '',
                  violationDetails: '',
                },
                isAddingEntry: false,
                violations: [...data.violations, newItem],
              };
            }

            return data;
          },
          title: 'Current Violations',
          validate: (data: CurrentViolationsFormData): boolean => {
            if (!data) return false;

            // hasCurrentViolations is always required
            // If answered false (no current violations), we're done
            // If answered true, must have at least one violation entry
            // (either saved violations OR a complete draft with date and details)
            return validateYesNoWithList(
              data.hasCurrentViolations,
              data.violations,
              data.draftViolation,
              (draft) => isNonEmptyString(draft.violationDate) && isNonEmptyString(draft.violationDetails),
              (draft) => !draft.violationDate && !draft.violationDetails,
            );
          },
        },
        {
          component: JobDismissalPage,
          defaultData: {
            dismissals: [],
            draftDismissal: {
              dismissalDate: '',
              dismissalDetails: '',
            },
            hasJobDismissal: null,
            isAddingEntry: false,
          },
          fieldValidation: {
            'draftDismissal.dismissalDate': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasJobDismissal !== true) return true;

                if (isDraftOptional(allData, 'dismissals', 'draftDismissal')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Date of the dismissal is required'),
            ],
            'draftDismissal.dismissalDetails': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasJobDismissal !== true) return true;

                if (isDraftOptional(allData, 'dismissals', 'draftDismissal')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Explanation of the dismissal is required'),
            ],
            hasJobDismissal: [requiredChoice],
          },
          id: SubStep.JOB_DISMISSAL,
          onBeforeNext: (data: JobDismissalFormData): JobDismissalFormData => {
            if (data.hasJobDismissal !== true) return data;

            const draft = data.draftDismissal;
            const isDraftEmpty = !draft.dismissalDate && !draft.dismissalDetails;
            const isDraftComplete = isNonEmptyString(draft.dismissalDate) && isNonEmptyString(draft.dismissalDetails);

            if (!isDraftEmpty && isDraftComplete) {
              const newItem = {
                data: { ...draft },
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };

              return {
                ...data,
                dismissals: [...data.dismissals, newItem],
                draftDismissal: {
                  dismissalDate: '',
                  dismissalDetails: '',
                },
                isAddingEntry: false,
              };
            }

            return data;
          },
          title: 'Job Dismissal',
          validate: (data: JobDismissalFormData): boolean => {
            if (!data) return false;

            // hasJobDismissal is always required
            // If answered false (no job dismissals), we're done
            // If answered true, must have at least one dismissal entry
            // (either saved dismissals OR a complete draft with date and details)
            return validateYesNoWithList(
              data.hasJobDismissal,
              data.dismissals,
              data.draftDismissal,
              (draft) => isNonEmptyString(draft.dismissalDate) && isNonEmptyString(draft.dismissalDetails),
              (draft) => !draft.dismissalDate && !draft.dismissalDetails,
            );
          },
        },
        {
          component: FederalDebtPage,
          defaultData: {
            draftFederalDebt: {
              debtDetails: '',
            },
            federalDebts: [],
            hasFederalDebt: null,
            isAddingEntry: false,
          },
          fieldValidation: {
            'draftFederalDebt.debtDetails': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasFederalDebt !== true) return true;

                if (isDraftOptional(allData, 'federalDebts', 'draftFederalDebt')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Details about the Federal debt are required'),
            ],
            hasFederalDebt: [requiredChoice],
          },
          id: SubStep.FEDERAL_DEBT,
          onBeforeNext: (data: FederalDebtFormData): FederalDebtFormData => {
            if (data.hasFederalDebt !== true) return data;

            const draft = data.draftFederalDebt;
            const isDraftEmpty = !draft.debtDetails;
            const isDraftComplete = isNonEmptyString(draft.debtDetails);

            if (!isDraftEmpty && isDraftComplete) {
              const newItem = {
                data: { ...draft },
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };

              return {
                ...data,
                draftFederalDebt: {
                  debtDetails: '',
                },
                federalDebts: [...data.federalDebts, newItem],
                isAddingEntry: false,
              };
            }

            return data;
          },
          title: 'Federal Debt',
          validate: (data: FederalDebtFormData): boolean => {
            if (!data) return false;

            // hasFederalDebt is always required
            // If answered false (no federal debt), we're done
            // If answered true, must have at least one debt entry
            // (either saved debts OR a complete draft with details)
            return validateYesNoWithList(
              data.hasFederalDebt,
              data.federalDebts,
              data.draftFederalDebt,
              (draft) => isNonEmptyString(draft.debtDetails),
              (draft) => !draft.debtDetails,
            );
          },
        },
        {
          component: RelativesPage,
          defaultData: {
            draftRelative: {
              relativeDetails: '',
            },
            hasRelatives: null,
            isAddingEntry: false,
            relatives: [],
          },
          fieldValidation: {
            'draftRelative.relativeDetails': [
              custom((value: unknown, allData?: Record<string, unknown>) => {
                if (allData?.hasRelatives !== true) return true;

                if (isDraftOptional(allData, 'relatives', 'draftRelative')) return true;

                return typeof value === 'string' && value.trim().length > 0;
              }, 'Details about the relative are required'),
            ],
            hasRelatives: [requiredChoice],
          },
          id: SubStep.RELATIVES,
          onBeforeNext: (data: RelativesFormData): RelativesFormData => {
            if (data.hasRelatives !== true) return data;

            const draft = data.draftRelative;
            const isDraftEmpty = !draft.relativeDetails;
            const isDraftComplete = isNonEmptyString(draft.relativeDetails);

            if (!isDraftEmpty && isDraftComplete) {
              const newItem = {
                data: { ...draft },
                id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };

              return {
                ...data,
                draftRelative: {
                  relativeDetails: '',
                },
                isAddingEntry: false,
                relatives: [...data.relatives, newItem],
              };
            }

            return data;
          },
          title: 'Relatives in Government',
          validate: (data: RelativesFormData): boolean => {
            if (!data) return false;

            // hasRelatives is always required
            // If answered false (no relatives working for the agency), we're done
            // If answered true, must have at least one relative entry
            // (either saved relatives OR a complete draft with details)
            return validateYesNoWithList(
              data.hasRelatives,
              data.relatives,
              data.draftRelative,
              (draft) => isNonEmptyString(draft.relativeDetails),
              (draft) => !draft.relativeDetails,
            );
          },
        },
        {
          component: RetirementPayPage,
          defaultData: {
            draftRetiredPay: {
              otherPayType: '',
              payType: '',
              receiptEndDate: '',
              receiptStartDate: '',
              stillReceivingPay: null,
            },
            hasRetirementPay: null,
            isAddingEntry: false,
            retiredPays: [],
          },
          fieldValidation: {
            'draftRetiredPay.otherPayType': [
              requiredInRetiredPayDraft(
                (draft) => draft.payType === 'other' && !isNonEmptyString(draft.otherPayType),
                'Other pay type is required',
              ),
            ],
            'draftRetiredPay.payType': [
              requiredInRetiredPayDraft((draft) => !isNonEmptyString(draft.payType), 'Type of pay is required'),
            ],
            'draftRetiredPay.receiptEndDate': [
              requiredInRetiredPayDraft(
                (draft) => draft.stillReceivingPay === false && !isNonEmptyString(draft.receiptEndDate),
                'Receipt end date is required',
              ),
            ],
            'draftRetiredPay.receiptStartDate': [
              requiredInRetiredPayDraft(
                (draft) => draft.stillReceivingPay === true && !isNonEmptyString(draft.receiptStartDate),
                'Receipt start date is required',
              ),
            ],
            'draftRetiredPay.stillReceivingPay': [
              requiredInRetiredPayDraft(
                (draft) => isNonEmptyString(draft.payType) && !isBooleanSet(draft.stillReceivingPay),
                'You must select whether you still receive this pay',
              ),
            ],
            hasRetirementPay: [requiredChoice],
          },
          id: SubStep.RETIREMENT_PAY,
          title: 'Retirement Pay',
          validate: (data: RetirementPayFormData): boolean => {
            if (!data) return false;

            // hasRetirementPay is always required. If answered yes, at least
            // one retired pay entry or complete draft must be present.
            return validateYesNoWithList(
              data.hasRetirementPay,
              data.retiredPays,
              data.draftRetiredPay,
              isCompleteRetiredPayDraft,
              isEmptyRetiredPayDraft,
            );
          },
        },
        {
          component: CertificationPage,
          defaultData: {
            certificationAccepted: false,
          },
          fieldValidation: {
            certificationAccepted: [
              custom((value: unknown) => {
                return value === true;
              }, 'You must check that you certify or you will not be able to submit your application.'),
            ],
          },
          id: SubStep.CERTIFICATION,
          title: 'Certification',
          validate: (data: CertificationFormData): boolean => {
            if (!data) return false;

            return data.certificationAccepted === true;
          },
        },
      ],
      title: 'Declaration of Federal Employment (OF-306) Questions',
    },
    {
      id: PrimaryStep.DOCUMENT_UPLOAD,
      subSteps: [
        {
          // ACP-2092: every document present but one still being scanned blocked with
          // "Some required information is missing" — untrue, and nothing to correct.
          blockedReason: (data: DocumentUploadFormData, allFormData: WorkflowFormData) =>
            isUploadScanInProgress(data, allFormData)
              ? {
                  heading: 'Your documents are still being scanned',
                  text: 'Wait for your documents to finish scanning before continuing.',
                }
              : undefined,

          component: DocumentUploadPage,

          defaultData: {
            legalDocuments: [],
            otherDocuments: [],
            passport: [],
            pivTrainingCertificate: [],
            residencyDocument: [],
            residencySupportingDocuments: [],
            resume: [],
            usVisaDocument: [],
          },

          fieldValidation: {
            passport: [requiredUpload((branch) => branch.isNonUSCitizen, 'Passport is required')],
            pivTrainingCertificate: [requiredUpload(() => true, 'PIV Training Certificate is required')],
            residencyDocument: [
              requiredUpload((branch) => branch.isPermanentResident, 'Residency document is required'),
            ],
            resume: [requiredUpload(() => true, 'Resume is required')],
            usVisaDocument: [
              requiredUpload(
                (branch) => branch.isNonUSCitizen && !branch.isPermanentResident,
                'Residency document is required',
              ),
            ],
          },

          id: SubStep.UPLOAD_DOCUMENTS,
          // The missing-document errors are announced, not drawn, so the page alert
          // stays as the visible cue — as it was before the per-document errors existed.
          keepIncompleteAlert: true,
          requiredFieldsText: 'uploads',
          title: 'Upload Documents',
          validate: (data: DocumentUploadFormData, allFormData: WorkflowFormData): boolean => {
            if (!data) return false;

            const { isNonUSCitizen, isPermanentResident } = uploadBranch(allFormData);

            // Required documents must have at least one valid file (without errors)
            const hasRequiredPivCert = hasAssociatedFile(data.pivTrainingCertificate);
            const hasRequiredResume = hasAssociatedFile(data.resume);

            const hasRequiredPassport = !isNonUSCitizen || hasAssociatedFile(data.passport);
            const hasRequiredVisa = !isNonUSCitizen || isPermanentResident || hasAssociatedFile(data.usVisaDocument);
            const hasRequiredResidency = !isPermanentResident || hasAssociatedFile(data.residencyDocument);

            return (
              !isUploadScanInProgress(data, allFormData) &&
              hasRequiredPivCert &&
              hasRequiredResume &&
              hasRequiredPassport &&
              hasRequiredVisa &&
              hasRequiredResidency
            );
          },
        },
      ],
      title: 'Document Upload',
    },
    {
      id: PrimaryStep.FINAL_REVIEW,
      subSteps: [
        {
          component: FinalReviewPage,
          defaultData: {},
          id: SubStep.REVIEW_APPLICATION,
          title: 'Review Application',
          validate: (): boolean => {
            return true;
          },
        },
      ],
      title: 'Final Review',
    },
    {
      id: PrimaryStep.CONFIRMATION,
      subSteps: [
        {
          component: ConfirmationPage,
          defaultData: {},
          id: SubStep.COMPLETION,
          title: 'Completion',
        },
      ],
      title: 'Confirmation',
    },
  ],
};
